import { useCallback, useEffect, useState } from 'react'
import type {
  AudioAnalysisSummary
} from '../../../api'
import type {
  AudioTrackLabel,
  AudioTrackLabelStateResult
} from '../../../api/labels'

type UseTrackLabelsInput = {
  initialAudioAnalysis: AudioAnalysisSummary
  onLoadTrackLabels: (trackId: string) => Promise<AudioTrackLabelStateResult>
  trackId: string
}

export const useTrackLabels = ({
  initialAudioAnalysis,
  onLoadTrackLabels,
  trackId
}: UseTrackLabelsInput) => {
  const [trackLabels, setTrackLabels] = useState<AudioTrackLabel[]>([])
  const [audioAnalysis, setAudioAnalysis] = useState<AudioAnalysisSummary>(initialAudioAnalysis)
  const [loadingTrackLabels, setLoadingTrackLabels] = useState(false)
  const [pendingRejectedSuggestionIds, setPendingRejectedSuggestionIds] = useState<string[]>([])

  const loadTrackLabels = useCallback(async (): Promise<AudioTrackLabelStateResult> => {
    setLoadingTrackLabels(true)
    try {
      const result = await onLoadTrackLabels(trackId)
      setTrackLabels(result.items)
      setAudioAnalysis(result.audio_analysis)
      return result
    } finally {
      setLoadingTrackLabels(false)
    }
  }, [onLoadTrackLabels, trackId])

  useEffect(() => {
    setAudioAnalysis(initialAudioAnalysis)
    setPendingRejectedSuggestionIds([])
    void loadTrackLabels()
  }, [initialAudioAnalysis, loadTrackLabels])

  const markSuggestionRejected = useCallback((assignmentId: string): void => {
    setPendingRejectedSuggestionIds((current) =>
      current.includes(assignmentId) ? current : [...current, assignmentId]
    )
  }, [])

  const clearPendingRejectedSuggestion = useCallback((assignmentId: string): void => {
    setPendingRejectedSuggestionIds((current) => current.filter((id) => id !== assignmentId))
  }, [])

  return {
    audioAnalysis,
    clearPendingRejectedSuggestion,
    loadingTrackLabels,
    loadTrackLabels,
    markSuggestionRejected,
    pendingRejectedSuggestionIds,
    setAudioAnalysis,
    trackLabels
  }
}
